import cn from 'clsx'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import OfferCalendarMonthPicker from './MonthPicker/OfferCalendarMonthPicker'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCalendarSidebarDayPicker from './DayPicker/OfferCalendarSidebarDayPicker'
import { getCalendarMonths, getPackagesForFilters, getSelectedOfferPageDay, isAnyFlightsActive, isFetchingCalendars } from 'selectors/multipleRoomBookingSelectors'
import OfferCalendarNotAvailableMessage from './OfferCalendarNotAvailableMessage'
import OfferCalendarNoFlightsMessage from './OfferCalendarNoFlightsMessage'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import OfferCalendarRoomSelect from './CalendarRooms/OfferCalendarRoomSelect'
import OfferCalendarDurationSelect from './CalendarDuration/OfferCalendarDurationSelect'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import CalendarPickerRecommendationsSidebar from './Recommendations/CalendarPickerRecommendationsSidebar'
import useDispatchPrecacheLere from 'components/Recommendations/hooks/useDispatchPrecacheLere'
import Group from 'components/utils/Group'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import TextButton from 'components/Luxkit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferCalenderDatePickerFooterPriceDetails from './OfferCalenderDatePickerFooterPriceDetails'
import OfferBookButton from 'components/OfferPage/OfferBookButton'
import { OfferPackageOptionView } from 'selectors/offerPageSelectors'
import { interactionStudioAPIController } from 'lib/interactionStudioUtils'
import { getInteractionStudioDataFromOfferPage } from 'selectors/interactionStudioSelectors'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import BodyText from 'components/Luxkit/Typography/BodyText'
import OfferCalendarFlightsToggle from './OfferCalendarFlightsToggle'
import noop from 'lib/noop'
import styled from 'styled-components'
import eases from 'styles/tools/eases'
import Carousel from 'components/Luxkit/Carousel/Carousel'
import { useTranslation } from 'react-i18next'

const FooterContents = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: unset;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

interface Props {
  currentOfferId: string;
  offer: App.Offer;
  airport: App.Airport;
  fetching?: boolean;
  flights?: boolean;
  checkIn?: moment.Moment;
  selectedDay?: App.CalendarDay;
  calendarError?: boolean;
  calendarAvailability: boolean;
  defaultDayPicker?: boolean;
  defaultMonth?: string;
  duration: number;
  rooms?: App.Occupants[];
  recommendationsShown: boolean;
  onBack?: () => void;
  onClose?: () => void;
  isAnyFlightsActive?: boolean;
  relevantPackages: Array<OfferPackageOptionView>;
  interactionStudioData?: App.ISACheckoutData;
  selectedPackageView: OfferPackageOptionView;
}

function OfferCalendarSidebarDatePicker(props: Props) {
  const {
    offer,
    fetching,
    flights,
    selectedDay,
    checkIn,
    calendarError,
    calendarAvailability,
    defaultDayPicker,
    defaultMonth,
    duration,
    recommendationsShown,
    rooms,
    onBack,
    onClose = noop,
    isAnyFlightsActive,
    currentOfferId,
    relevantPackages,
    interactionStudioData,
    selectedPackageView,
  } = props
  const { currentRegionHasFlights } = useContext(GeoContext)
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const { t, i18n: { language } } = useTranslation(['common', 'offerPage'])

  const [selectedMonth, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)
  const dispatch = useOfferPageDispatch()
  const onMonthSelect = useCallback((monthKey: string) => {
    setMonth(monthKey)
  }, [])

  useEffect(() => {
    if (checkIn && selectedDay && !selectedDay.hotelAvailable) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    }
  }, [checkIn, dispatch, selectedDay, currentOfferId])

  const onBackToMonth = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    setMonth(undefined)
  }, [onUserAnalyticsAction, dispatch, currentOfferId])

  const flightsEnabled = currentRegionHasFlights && offer.offerFlightsEnabled && !offer.bundledWithFlightsOnly

  useDispatchPrecacheLere(true, offer, duration, rooms)

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch])

  const onBookNowButtonClick = useCallback(() => {
    interactionStudioAPIController('checkin', interactionStudioData)
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.BookNow)
  }, [interactionStudioData, onUserAnalyticsAction])

  const onApply = useCallback(() => {
    interactionStudioAPIController('checkin', interactionStudioData!)
    onClose()
  }, [interactionStudioData, onClose])

  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
  const canBook = selectedPackageView && !useDynamicCancellationPolicies
  const hasSelectedMonth = selectedMonth !== undefined

  const dateFormatter = Intl.DateTimeFormat(language, INTL_SHORT_DAY_NAME_DAY_MONTH)

  return (<>
    <ModalHeader
      title={hasSelectedMonth ? t('Select check-in dates', { ns: 'offerPage' }) : t('Select check-in month', { ns: 'offerPage' })}
      onCloseButtonClick={onClose}
      onBackButtonClick={onBack}
    >
      {!recommendationsShown && <Carousel gap={8} gutterStyle="overflow">
        <OfferCalendarRoomSelect />
        <OfferCalendarDurationSelect />
        {flightsEnabled && <OfferCalendarFlightsToggle />}
      </Carousel>}
    </ModalHeader>
    <ModalBody>
      <ModalContent>
        {recommendationsShown && <CalendarPickerRecommendationsSidebar />}
        {!recommendationsShown && <>
          {!fetching && (calendarError || !flights) && !calendarAvailability && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
          {!fetching && !calendarError && flights && !calendarAvailability && <OfferCalendarNoFlightsMessage canSwitchToHotel={!offer?.bundledWithFlightsOnly} />}
          {calendarAvailability && <>
            {hasSelectedMonth && !fetching && <OfferCalendarSidebarDayPicker
              onBackToMonth={onBackToMonth}
              initialMonth={selectedMonth}
              defaultDayPicker={defaultDayPicker}
              flightsOnly={offer.bundledWithFlightsOnly}
            />}
            {!hasSelectedMonth && <OfferCalendarMonthPicker
              onMonthSelect={onMonthSelect}
            />}
          </>}
        </>}
      </ModalContent>
    </ModalBody>
    <FooterContents className={cn({ visible: !!selectedDay })}>
      <ModalFooter>
        <VerticalSpacer gap={8}>
          {selectedDay && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            <BodyText variant="medium">
              <Group direction="horizontal" verticalAlign="center">
                <span>{dateFormatter.format(new Date(selectedDay.checkIn))}</span>
                <LineArrowRightIcon size="XS" />
                <span>{dateFormatter.format(new Date(selectedDay.checkOut))}</span>
              </Group>
            </BodyText>
            <TextButton kind="tertiary" outdent="right" onClick={onResetDates}>
              {t('Reset', { ns: 'common' })}
            </TextButton>
          </Group>}
          <Group direction="horizontal" horizontalAlign="space-between">
            <div>
              {selectedDay && <OfferCalenderDatePickerFooterPriceDetails
                offer={offer}
                duration={duration}
                selectedDay={selectedDay}
                isAnyFlightsActive={isAnyFlightsActive}
                isFromPrice={relevantPackages.length > 1}
              />}
            </div>
            {canBook && <OfferBookButton
              size="large"
              data-buytype={isAnyFlightsActive ? 'flights' : undefined}
              pkg={selectedPackageView.package}
              onSubmit={onBookNowButtonClick}
            >
              {t('Book now', { ns: 'common' })}
            </OfferBookButton>}
            {!canBook && <TextButton kind="primary" size="large" onClick={onApply}>
              {t('Continue', { ns: 'common' })}
            </TextButton>}
          </Group>
        </VerticalSpacer>
      </ModalFooter>
    </FooterContents>
  </>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    currentOfferId: state.paginator?.currentOfferId,
    airport: state.airport,
    calendarAvailability: getCalendarMonths(state, appState).availableCount > 0,
    calendarError: state.offer?.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    checkIn: state.checkInDate,
    fetching: isFetchingCalendars(state, appState),
    flights: state.flights,
    offer: state.offer,
    recommendationsShown: state.showRecommendationsView && shouldEnableCalendarRecommendations(appState, state.offer),
    selectedDay: getSelectedOfferPageDay(state, appState),
    duration: state.duration,
    isAnyFlightsActive: isAnyFlightsActive(state, appState),
    rooms: state.rooms,
    relevantPackages: getPackagesForFilters(state, appState),
    interactionStudioData: getInteractionStudioDataFromOfferPage(appState, state),
    selectedPackageView: state.selectedPackageView,
  }
})(OfferCalendarSidebarDatePicker)
